import { forwardRef, useEffect } from 'react';
import { PlusSquareIcon, QuotesIcon } from '@phosphor-icons/react';

/** Two icon buttons, their gap and the padding around them. */
const TOOLBAR_WIDTH = 66;

/** Keeps the toolbar inside its container when the selection it points at runs to the right edge —
 * which a right-aligned user bubble in the thread always does. */
export function anchorLeft(x: number, containerWidth: number): number {
  return Math.max(0, Math.min(x, containerWidth - TOOLBAR_WIDTH));
}

function ToolbarButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // Without this the press first collapses the selection the click is about to act on.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-md text-fg-secondary hover:bg-surface-3 hover:text-fg"
    >
      {children}
    </button>
  );
}

/**
 * Floating toolbar shown beside a selection — in a code view, a diff, the thread or the plan —
 * offering to attach it to the next chat message.
 *
 * Two gestures, because attaching alone only ever says "look here". That is enough when the next
 * thing typed is a fresh instruction and useless when it is an answer to something: three
 * questions in a row, attached bare, leave the agent to work out which reply belongs to which. So
 * the first button attaches *and* opens the note field on the chip it just staged, which is where
 * the answer gets written — against the passage, not pooled at the end of the message.
 *
 * The note is written up at the composer rather than here on purpose. A panel with a textarea and
 * a button is a form dropped on top of the thing you were reading, and it appears on every drag;
 * two icons don't cover the passage they point at, and the typing happens where the rest of the
 * message is being written.
 *
 * Position is left to the caller — a code viewer, a diff and the thread all compute it
 * differently (CodeMirror coords vs. a native `Range` rect). The forwarded ref goes on the whole
 * toolbar, so a caller that tracks selection through native mouse events can tell a click that
 * landed inside it from one that should dismiss it.
 */
export const SelectionToolbar = forwardRef<
  HTMLDivElement,
  {
    style: React.CSSProperties;
    /** Stage the selection. `annotate` also opens its chip's note field in the composer, so the
     * next keystroke goes into a note about this passage rather than into the message body. */
    onAdd: (annotate: boolean) => void;
    onCancel: () => void;
    /** What writing about this is called here. Defaults to the generic gesture; the plan pane
     * calls it commenting, which is what the reader thinks they are doing even though it stages
     * context like everything else. */
    annotateLabel?: string;
  }
>(({ style, onAdd, onCancel, annotateLabel = 'Add to chat with a note' }, ref) => {
  // Nothing in here takes focus — that is the point of a toolbar over a form — so Escape has to
  // be caught at the document to stay an escape hatch.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      ref={ref}
      style={style}
      className="absolute z-10 flex items-center gap-0.5 rounded-lg border border-line-strong bg-surface-2 p-0.5 shadow-(--shadow-overlay)"
    >
      <ToolbarButton label={annotateLabel} onClick={() => onAdd(true)}>
        <QuotesIcon size={14} />
      </ToolbarButton>
      <ToolbarButton label="Add to chat" onClick={() => onAdd(false)}>
        <PlusSquareIcon size={14} />
      </ToolbarButton>
    </div>
  );
});
SelectionToolbar.displayName = 'SelectionToolbar';
